import {Will} from "../../model/Will";
import FormPageSection from "../../components/FormPageSection/FormPageSection";
import TextAreaField from "../../components/FormContent/TextAreaField/TextAreaField";
import {WILL_DETAILS_CONSTANTS as Labels} from "../../constants/LabelConstants";
import DropdownField from "../../components/FormContent/DropdownField/DropdownField";
import React, {useEffect, useState} from "react";
import {
    FiduciaryAssignment,
    FiduciaryKey,
    FiduciaryMemberType,
    FiduciarySummary,
    State
} from "../../model/EstateFlowchartTypes";
import {useAppSelector} from "../../../store/hooks";
import {listOfStatesSelector} from "../../slices/EstateFlowChartSlice";
import {parseStates} from "../../utils/DefUtils";
import SuccessorTable from "../../components/SuccessorTable/SuccessorTable";
import LegalDocumentsSection from "./LegalDocumentsSection";

interface WillDetailsSectionProps {
    estateItem: Will;
    updateEstateItem: (estateItem: Will) => void;
    fiduciaries: FiduciarySummary;
    updateFiduciaries: (fiduciarySummary: FiduciarySummary) => void;
}

function WillDetailsSection({
                                estateItem,
                                updateEstateItem,
                                fiduciaries,
                                updateFiduciaries
                            }: Readonly<WillDetailsSectionProps>) {
    const unitedStates = useAppSelector(listOfStatesSelector);
    const [parsedStates, setParsedStates] = useState<State[]>([]);
    const [fiduciaryAssignment, setFiduciaryAssignment] = useState<FiduciaryAssignment>({} as FiduciaryAssignment);

    const getFiduciaryAssignment = (): FiduciaryAssignment => {
        const willOwnerKey = fiduciaries.primaryMember?.assignedToMemberId === estateItem.willOwner
            ? "primaryMember"
            : "partnerMember";
        return fiduciaries[willOwnerKey] ?? {} as FiduciaryAssignment;
    };

    useEffect(() => {
        if (fiduciaries.primaryMember) {
            setFiduciaryAssignment(getFiduciaryAssignment());
        }
    }, [fiduciaries, estateItem.willOwner]);

    useEffect(() => {
        setParsedStates(parseStates(unitedStates));
    }, [unitedStates]);

    const handleFiduciaryUpdate = (updatedAssignment: FiduciaryAssignment) => {
        if (fiduciaries.primaryMember) {
            const willOwnerKey = fiduciaries.primaryMember?.assignedToMemberId === estateItem.willOwner
                ? "primaryMember"
                : "partnerMember";
            const updatedFiduciaries: FiduciarySummary = {
                ...fiduciaries,
                [willOwnerKey]: updatedAssignment
            };
            setFiduciaryAssignment(updatedAssignment);
            updateFiduciaries(updatedFiduciaries);
        }
    };

    return (
        <FormPageSection id={"will-details-section"}
                         headerText={Labels.WILL_DETAILS_SECTION_LABEL_TEXT}
                         showRequiredLabel={false}>

            <SuccessorTable formData={fiduciaryAssignment}
                            successorType={FiduciaryMemberType.Executor}
                            updateData={handleFiduciaryUpdate}
                            title={Labels.EXECUTOR_LABEL_TEXT}
                            buttonText={"ADD EXECUTOR"}
                            rowName={"NAME"}
                            customKey={FiduciaryKey.Executors}/>

            <SuccessorTable formData={fiduciaryAssignment}
                            successorType={FiduciaryMemberType.Guardian}
                            updateData={handleFiduciaryUpdate}
                            title={Labels.GUARDIANS_FOR_MINORS_LABEL_TEXT}
                            buttonText={"ADD GUARDIAN"}
                            rowName={"NAME"}
                            includeNT={false}
                            customKey={FiduciaryKey.GuardiansForMinors}
            />

            <TextAreaField input={estateItem.funding}
                           label={Labels.FUNDING_LABEL_TEXT}
                           subText={Labels.FUNDING_SUB_TEXT}
                           onChange={(funding) => updateEstateItem({
                               ...estateItem,
                               funding
                           })}/>

            <TextAreaField input={estateItem.exercisesOfPoa}
                           label={Labels.EXERCISES_OF_POA_LABEL_TEXT}
                           subText={Labels.EXERCISES_OF_POA_SUB_TEXT}
                           onChange={(exercisesOfPoa) => updateEstateItem({
                               ...estateItem,
                               exercisesOfPoa
                           })}/>

            <TextAreaField input={estateItem.personalProperty}
                           label={Labels.TANGIBLE_PERSONAL_PROPERTY_LABEL_TEXT}
                           onChange={(personalProperty) => updateEstateItem({
                               ...estateItem,
                               personalProperty
                           })}/>

            <TextAreaField input={estateItem.bequests}
                           label={Labels.SPECIFIC_BEQUESTS_LABEL_TEXT}
                           onChange={(bequests) => updateEstateItem({
                               ...estateItem,
                               bequests
                           })}/>

            <DropdownField value={estateItem.jurisdiction ?? ""}
                           label={Labels.JURISDICTION_LABEL_TEXT}
                           items={parsedStates}
                           onChange={(jurisdiction) => {
                               if (jurisdiction !== "") {
                                   updateEstateItem({
                                       ...estateItem,
                                       jurisdiction
                                   })
                               }
                           }}/>

            <LegalDocumentsSection/>
        </FormPageSection>);
}

export default WillDetailsSection;